import {
  AfterViewInit,
  Component,
  DOCUMENT,
  ElementRef,
  forwardRef,
  inject,
  input,
  OnInit,
  viewChild,
  ViewEncapsulation,
  DestroyRef,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NgControl,
  TouchedChangeEvent,
  ValidationErrors
} from '@angular/forms';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import intlTelInput from 'intl-tel-input';
import { filter, startWith } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import { AppInputDirective } from '../../../../legacy/form/controls/input/input.directive';
import { AppFormFieldControl } from '../../../../legacy/form/form-field/form-field-control/form-field-control';

/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */

/* eslint-disable @typescript-eslint/no-unsafe-member-access */

@Component({
  selector: 'app-phone-input',
  templateUrl: './phone-input.component.html',
  styleUrls: ['./phone-input.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: AppFormFieldControl,
      useExisting: forwardRef(() => PhoneInputComponent)
    }
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [AppInputDirective, FormsModule, TranslatePipe]
})
export class PhoneInputComponent
  implements OnInit, ControlValueAccessor, AfterViewInit
{
  #destroyRef = inject(DestroyRef);
  #document = inject(DOCUMENT);
  public ngControl = inject(NgControl, { self: true, optional: true });
  readonly required = input(false);
  readonly placeholder = input('general.phone_national_placeholder_l');
  readonly id = input('');
  readonly count = input(false);
  readonly readonly = input(false);

  readonly preferredCountries = input([
    'de',
    'at',
    'ch',
    'nl',
    'fr',
    'pl',
    'es',
    'it',
    'uk'
  ]);

  readonly initialCountry = input('de');
  /**
   * Possibility to enter the phone number in national format
   * Input Example Germany: 0151 11223344
   * Output still E.164: +4915111223344
   */
  readonly nationalMode = input(true);

  /**
   * render the country dropdown detached (via fixed positioning) instead
   * useful when the input is rendered inside a modal
   */
  readonly detachCountryDropdown = input(false);

  readonly phoneInput = viewChild<ElementRef<HTMLInputElement>>('phoneInput');

  private intlTelInput;

  // Needed for AppFormFieldControl to display the error messages
  stateChanges: Subject<void> = new Subject<void>();
  errors: ValidationErrors | null = null;
  touched = false;
  value: string = null;

  private onChange = (value: string) => value;
  private onTouch = () => null;

  constructor() {
    if (this.ngControl) {
      // Note: we provide the value accessor through here, instead of
      // the `providers` to avoid running into a circular import.
      // Ref: https://github.com/angular/components/blob/main/src/material/select/select.ts#L618
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit() {
    if (this.ngControl) {
      // Sync `errors` & `touched` state with the values needed for AppFormFieldControl
      this.ngControl.control.statusChanges
        ?.pipe(
          // Use startWith to set the values initially once
          startWith(null),
          takeUntilDestroyed(this.#destroyRef)
        )
        .subscribe(() => {
          this.errors = this.ngControl.errors;
          this.stateChanges.next();
        });
      this.ngControl.control.events
        .pipe(
          // Use startWith to set the values initially once
          startWith(null),
          filter(event => event instanceof TouchedChangeEvent),
          takeUntilDestroyed(this.#destroyRef)
        )
        .subscribe(() => {
          this.touched = this.ngControl.control?.touched ?? false;
          this.stateChanges.next();
        });
    }
  }

  ngAfterViewInit(): void {
    const {
      preferredCountries: preferredCountriesInput,
      initialCountry: initialCountryInput,
      nationalMode: nationalModeInput
    } = this;
    const preferredCountries = preferredCountriesInput();
    const initialCountry = initialCountryInput();
    const nationalMode = nationalModeInput();
    /**
     * The utilities script is a custom build of Google's libphonenumber which enables the following features:
     *
     * Formatting upon initialisation, as well as with getNumber and setNumber
     * Validation with isValidNumber, getNumberType and getValidationError methods
     * Placeholder set to an example number for the selected country - even
     * specify the type of number (e.g. mobile) using the placeholderNumberType option
     * Extract the standardised (E.164) international number with getNumber even when using the nationalMode option
     */
    // Load the utils bundled with the installed intl-tel-input rather than from
    // a CDN: the previous hardcoded jsdelivr URL pinned 24.5.0 against a 24.8.2
    // library, and under jsdom Node's ESM loader rejects `import('https://...')`,
    // which failed any test rendering this component.
    const loadUtilsOnInit = () => import('intl-tel-input/utils');

    const nativeInput = this.phoneInput().nativeElement;

    const options = {
      loadUtilsOnInit,
      initialCountry,
      preferredCountries,
      nationalMode,
      ...(this.detachCountryDropdown()
        ? {
            dropdownContainer:
              nativeInput.closest('dialog') ?? this.#document.body
          }
        : {}),
      autoPlaceholder: 'off'
    };

    this.intlTelInput = intlTelInput(nativeInput, options);

    nativeInput.addEventListener('countrychange', () => this.onInput());
  }

  onInput() {
    const noWhitespace = this.value?.replace(' ', '');
    const onlyNumbersAndPlus = noWhitespace?.replace(/[^0-9 +]/g, '');
    const only15Digits = onlyNumbersAndPlus?.substring(0, 15);
    setTimeout(() => {
      this.value = only15Digits;
    });
    const intlNumber: string = this.intlTelInput.getNumber();
    this.onChange(intlNumber);
  }

  registerOnChange(fn): void {
    this.onChange = fn;
  }

  registerOnTouched(fn): void {
    this.onTouch = fn;
  }

  public writeValue(value: string): void {
    this.value = value ?? '';
    if (this.intlTelInput) {
      this.intlTelInput.setNumber(value ?? '');
    }
    //this.cdr.detectChanges();
  }

  onFocusOut(): void {
    this.onTouch();
    if (this.ngControl?.control?.errors) {
      this.stateChanges.next();
    }
  }
}
